import React from "react";
import { X, Loader2, Search } from "lucide-react";

const COUNTRIES = [
  { code: "IN", name: "India" }, { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" }, { code: "AE", name: "UAE" },
  { code: "SG", name: "Singapore" }, { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" }, { code: "DE", name: "Germany" },
  { code: "JP", name: "Japan" }, { code: "FR", name: "France" },
  { code: "CN", name: "China" }, { code: "RU", name: "Russia" },
  { code: "BR", name: "Brazil" }, { code: "ZA", name: "South Africa" },
  { code: "NG", name: "Nigeria" }, { code: "KE", name: "Kenya" },
  { code: "PK", name: "Pakistan" }, { code: "BD", name: "Bangladesh" },
  { code: "LK", name: "Sri Lanka" }, { code: "NP", name: "Nepal" },
  { code: "MY", name: "Malaysia" }, { code: "ID", name: "Indonesia" },
  { code: "TH", name: "Thailand" }, { code: "VN", name: "Vietnam" },
  { code: "PH", name: "Philippines" }, { code: "SA", name: "Saudi Arabia" },
  { code: "QA", name: "Qatar" }, { code: "KW", name: "Kuwait" },
  { code: "BH", name: "Bahrain" }, { code: "OM", name: "Oman" },
  { code: "IR", name: "Iran" }, { code: "AF", name: "Afghanistan" },
  { code: "KP", name: "North Korea" }, { code: "MM", name: "Myanmar" },
  { code: "SY", name: "Syria" }, { code: "YE", name: "Yemen" },
];

const ID_TYPES = [
  { value: "", label: "Select ID Type" },
  { value: "PAN", label: "PAN Card" },
  { value: "AADHAAR", label: "Aadhaar Card" },
  { value: "PASSPORT", label: "Passport" },
  { value: "VOTER_ID", label: "Voter ID" },
  { value: "DL", label: "Driving License" },
];

const CHECK_OPTIONS = [
  { key: "kyc", label: "KYC Verification" },
  { key: "sanctions", label: "Sanctions" },
  { key: "pep", label: "PEP" },
  { key: "adverse_media", label: "Adverse Media" },
];

const SCREENING_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "corporate", label: "Corporate" },
  { value: "director", label: "Director" },
];

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;

export function NewScreeningForm({ form, setForm, onSubmit, onClose, submitting }) {
  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const toggleCheck = (key) => {
    setForm((prev) => {
      const checks = prev.checks.includes(key)
        ? prev.checks.filter((c) => c !== key)
        : [...prev.checks, key];
      return { ...prev, checks };
    });
  };

  const screeningType = form.screeningType || "individual";
  const panValid = !form.panNumber || PAN_REGEX.test(form.panNumber.toUpperCase());
  const gstValid = !form.gstNumber || GST_REGEX.test(form.gstNumber.toUpperCase());

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 50,
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      paddingTop: "40px", overflowY: "auto",
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "#0d1117", border: "1px solid #1e2530", borderRadius: "14px",
        width: "100%", maxWidth: "600px", padding: "28px", marginBottom: "40px",
        animation: "slideDown 0.25s ease-out",
      }} data-testid="new-screening-modal">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#f1f5f9" }}>New Screening</h2>
          <button onClick={onClose} data-testid="close-screening-modal"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#475569", padding: "4px" }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Screening Type */}
          <div>
            <label style={labelStyle}>Screening Type</label>
            <div className="flex gap-2">
              {SCREENING_TYPES.map(t => (
                <button key={t.value} onClick={() => updateField("screeningType", t.value)}
                  data-testid={`stype-${t.value}`}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: "8px", cursor: "pointer",
                    border: screeningType === t.value ? "1px solid #2563eb" : "1px solid #1e2530",
                    background: screeningType === t.value ? "rgba(37,99,235,0.1)" : "#080c12",
                    color: screeningType === t.value ? "#60a5fa" : "#94a3b8",
                    fontSize: "12px", fontWeight: 600, textAlign: "center",
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label style={labelStyle}>
              {screeningType === "corporate" ? "Company Name *" : screeningType === "director" ? "Director Name *" : "Full Name *"}
            </label>
            <input type="text" value={form.fullName} onChange={(e) => updateField("fullName", e.target.value)}
              placeholder={screeningType === "corporate" ? "e.g., Tata Consultancy Services Ltd" : "e.g., Rajesh Kumar Sharma"}
              className="input-aml w-full" data-testid="screening-fullname" autoFocus />
          </div>

          {/* DOB + Nationality */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>{screeningType === "corporate" ? "Incorporation Date" : "Date of Birth"}</label>
              <input type="date" value={form.dateOfBirth} onChange={(e) => updateField("dateOfBirth", e.target.value)}
                className="input-aml w-full" data-testid="screening-dob" style={{ colorScheme: "dark" }} />
            </div>
            <div>
              <label style={labelStyle}>{screeningType === "corporate" ? "Country of Registration" : "Nationality"}</label>
              <select value={form.nationality} onChange={(e) => updateField("nationality", e.target.value)}
                className="input-aml w-full" data-testid="screening-nationality" style={{ colorScheme: "dark" }}>
                <option value="">Select Country</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* ID Type + Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>ID Type</label>
              <select value={form.idType} onChange={(e) => updateField("idType", e.target.value)}
                className="input-aml w-full" data-testid="screening-idtype" style={{ colorScheme: "dark" }}>
                {ID_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>ID Number</label>
              <input type="text" value={form.idNumber} onChange={(e) => updateField("idNumber", e.target.value)}
                placeholder={form.idType === "PAN" ? "ABCDE1234F" : form.idType === "AADHAAR" ? "123456789012" : "Enter number"}
                className="input-aml w-full" data-testid="screening-idnumber"
                disabled={!form.idType} style={{ opacity: form.idType ? 1 : 0.4 }} />
            </div>
          </div>

          {/* === India-Specific Fields === */}
          <div style={{ borderTop: "1px solid #1e2530", paddingTop: "12px" }}>
            <label style={{ ...labelStyle, fontSize: "10px", textTransform: "uppercase", letterSpacing: "1px", color: "#475569" }}>
              India-Specific Identifiers (Optional)
            </label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {/* Individual: PAN + Aadhaar */}
              {(screeningType === "individual" || screeningType === "director") && (
                <>
                  <div>
                    <label style={{ ...labelStyle, fontSize: "10px" }}>PAN Number</label>
                    <input type="text" value={form.panNumber || ""} maxLength={10}
                      onChange={(e) => updateField("panNumber", e.target.value.toUpperCase())}
                      placeholder="ABCDE1234F" className="input-aml w-full" data-testid="screening-pan"
                      style={{ borderColor: !panValid ? "#ef4444" : undefined }} />
                    {!panValid && <span style={{ fontSize: "9px", color: "#ef4444" }}>Invalid PAN format</span>}
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: "10px" }}>Aadhaar Last 4</label>
                    <input type="text" value={form.aadhaarLast4 || ""} maxLength={4}
                      onChange={(e) => updateField("aadhaarLast4", e.target.value.replace(/\D/g, ""))}
                      placeholder="1234" className="input-aml w-full" data-testid="screening-aadhaar" />
                  </div>
                </>
              )}

              {/* Corporate: GST + CIN */}
              {screeningType === "corporate" && (
                <>
                  <div>
                    <label style={{ ...labelStyle, fontSize: "10px" }}>GST Number</label>
                    <input type="text" value={form.gstNumber || ""} maxLength={15}
                      onChange={(e) => updateField("gstNumber", e.target.value.toUpperCase())}
                      placeholder="27AAAAA1234A1Z5" className="input-aml w-full" data-testid="screening-gst"
                      style={{ borderColor: !gstValid ? "#ef4444" : undefined }} />
                    {!gstValid && <span style={{ fontSize: "9px", color: "#ef4444" }}>Invalid GST format</span>}
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: "10px" }}>CIN</label>
                    <input type="text" value={form.cinNumber || ""}
                      onChange={(e) => updateField("cinNumber", e.target.value.toUpperCase())}
                      placeholder="U17110MH2015PTC123456" className="input-aml w-full" data-testid="screening-cin" />
                  </div>
                </>
              )}

              {/* Director: DIN */}
              {screeningType === "director" && (
                <div>
                  <label style={{ ...labelStyle, fontSize: "10px" }}>Director DIN</label>
                  <input type="text" value={form.dinNumber || ""} maxLength={8}
                    onChange={(e) => updateField("dinNumber", e.target.value.replace(/\D/g, ""))}
                    placeholder="12345678" className="input-aml w-full" data-testid="screening-din" />
                </div>
              )}
            </div>
          </div>

          {/* Checks */}
          <div>
            <label style={labelStyle}>Checks to Run</label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              {CHECK_OPTIONS.map((opt) => {
                const active = form.checks.includes(opt.key);
                const disabled = opt.key === "kyc" && !form.idType;
                return (
                  <label key={opt.key} data-testid={`check-toggle-${opt.key}`}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "10px 14px", borderRadius: "8px", cursor: disabled ? "not-allowed" : "pointer",
                      border: active ? "1px solid #2563eb" : "1px solid #1e2530",
                      background: active ? "rgba(37,99,235,0.08)" : "#080c12",
                      opacity: disabled ? 0.35 : 1, transition: "all 0.15s",
                    }}>
                    <input type="checkbox" checked={active} disabled={disabled}
                      onChange={() => !disabled && toggleCheck(opt.key)}
                      style={{ accentColor: "#2563eb", width: 16, height: 16 }} />
                    <span style={{ fontSize: "13px", color: active ? "#60a5fa" : "#94a3b8", fontWeight: 500 }}>
                      {opt.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button onClick={onSubmit} disabled={submitting || !form.fullName.trim() || form.checks.length === 0}
            className="btn-primary flex-1" data-testid="submit-screening"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              opacity: submitting || !form.fullName.trim() || form.checks.length === 0 ? 0.5 : 1,
              padding: "12px",
            }}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {submitting ? "Running Screening..." : "Run Screening"}
          </button>
          <button onClick={onClose}
            style={{
              background: "transparent", border: "1px solid #1e2530", borderRadius: "8px",
              padding: "12px 20px", color: "#94a3b8", cursor: "pointer", fontSize: "13px",
            }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  fontSize: "12px", color: "#94a3b8", marginBottom: "6px", display: "block", fontWeight: 500,
};
